import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { loadTranslations } from '@angular/localize';
import { firstValueFrom } from 'rxjs';

const LOCALE_LOCALSTORAGE_KEY = 'e5190deccf484eea9d9676c8fd75dbc0';

const LANGUAGE_OPTIONS = ['cs', 'en', 'fr', 'nl', 'de', 'es-419'] as const;
type SupportedLocale = (typeof LANGUAGE_OPTIONS)[number];

declare const $localize: { locale?: string };

@Injectable({ providedIn: 'root' })
export class LocalizeBootstrapService {
  private readonly translate = inject(TranslateService);

  async init(): Promise<void> {
    const locale = this.detectLocale();
    $localize.locale = locale;
    this.translate.use(locale);

    try {
      const translations = await firstValueFrom(this.translate.getTranslation(locale));
      const flat = this.flatten(translations as Record<string, unknown>);
      if (Object.keys(flat).length > 0) {
        loadTranslations(flat);
      }
    } catch {
      // Translations unreachable; i18n attributes fall back to the default text
      // baked into each template at build time.
    }
  }

  private detectLocale(): SupportedLocale {
    return this.readFromLocalStorage() ?? this.detectBrowserLocale() ?? 'en';
  }

  private readFromLocalStorage(): SupportedLocale | null {
    if (typeof window === 'undefined') return null;
    try {
      const stored = window.localStorage?.getItem(LOCALE_LOCALSTORAGE_KEY);
      return this.matchLanguage(stored?.toLowerCase() ?? '');
    } catch {
      return null;
    }
  }

  private detectBrowserLocale(): SupportedLocale | null {
    if (typeof navigator === 'undefined') return null;
    const raw = (navigator.languages?.[0] || navigator.language || '')
      .toLowerCase()
      .replace(/_/g, '-');
    return this.matchLanguage(raw);
  }

  private matchLanguage(code: string): SupportedLocale | null {
    if (!code) return null;
    const exact = LANGUAGE_OPTIONS.find((l) => l.startsWith(code));
    if (exact) return exact;
    if (code.length > 2 && code[2] === '-') {
      const partial = LANGUAGE_OPTIONS.find((l) => l.startsWith(code.slice(0, 2)));
      if (partial) return partial;
    }
    return null;
  }

  private flatten(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
    const out: Record<string, string> = {};
    for (const key of Object.keys(obj)) {
      const value = obj[key];
      const path = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object') {
        Object.assign(out, this.flatten(value as Record<string, unknown>, path));
      } else {
        out[path] = String(value);
      }
    }
    return out;
  }
}
